<template>
  <div class="speed-curve-panel">
    <div class="speed-curve-panel__toolbar">
      <button type="button" class="speed-curve-panel__settings" @click="settingsVisible = true">
        <i class="el-icon-setting"></i>
        <span>设置</span>
      </button>
    </div>
    <div ref="chartEl" class="speed-curve-panel__chart" :style="{ height }"></div>
    <SpeedCurveSettingsDialog
      :visible.sync="settingsVisible"
      @saved="onSettingsSaved"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { buildQualityChartSeries } from '@/utils/trackQuality'
import SpeedCurveSettingsDialog from '@/components/SpeedCurveSettingsDialog.vue'
import {
  getSpeedCurveSettings,
  onSpeedCurveSettingsChange
} from '@/utils/speedCurveSettings'

const OVERSPEED_LINE_COLOR = '#FF1F1F'

export default {
  name: 'SpeedCurvePanel',
  components: { SpeedCurveSettingsDialog },
  props: {
    qualityMode: { type: Boolean, default: false },
    points: {
      type: Array,
      default: () => []
    },
    active: {
      type: Boolean,
      default: true
    },
    height: {
      type: String,
      default: '33rem'
    }
  },
  data() {
    return {
      chart: null,
      zoomStart: 0,
      zoomEnd: 100,
      settingsVisible: false,
      overspeedLimit: getSpeedCurveSettings().overspeedLimit,
      unsubSettings: null
    }
  },
  watch: {
    active(val) {
      if (val) this.renderChart()
    },
    height() {
      if (this.active) this.$nextTick(() => this.renderChart())
    },
    points: {
      handler(newVal, oldVal) {
        const newLen = Array.isArray(newVal) ? newVal.length : 0
        const oldLen = Array.isArray(oldVal) ? oldVal.length : 0
        const newFirst = newLen ? newVal[0].time : ''
        const oldFirst = oldLen ? oldVal[0].time : ''
        if (newLen !== oldLen || newFirst !== oldFirst) {
          this.zoomStart = 0
          this.zoomEnd = 100
        }
        this.renderChart()
      },
      deep: true
    }
  },
  mounted() {
    this.unsubSettings = onSpeedCurveSettingsChange((cfg) => {
      this.overspeedLimit = cfg.overspeedLimit
      this.renderChart()
    })
    this._onResize = () => {
      if (this.chart) this.chart.resize()
    }
    window.addEventListener('resize', this._onResize)
    if (this.active) this.renderChart()
  },
  beforeDestroy() {
    if (this.unsubSettings) this.unsubSettings()
    window.removeEventListener('resize', this._onResize)
    this.disposeChart()
  },
  methods: {
    onSettingsSaved() {
      const cfg = getSpeedCurveSettings()
      this.overspeedLimit = cfg.overspeedLimit
      this.renderChart()
      this.$emit('settings-saved', cfg)
    },
    disposeChart() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    initChart() {
      const el = this.$refs.chartEl
      if (!el) return null
      if (!this.chart) {
        this.chart = echarts.init(el)
        this.chart.on('datazoom', (params) => {
          const batch = params && Array.isArray(params.batch) ? params.batch : null
          const source = batch && batch.length ? batch[0] : params
          if (!source) return
          if (source.start != null) this.zoomStart = source.start
          if (source.end != null) this.zoomEnd = source.end
        })
        this.chart.getZr().on('click', (event) => {
          const pixel = [event.offsetX, event.offsetY]
          if (!this.chart.containPixel({ gridIndex: 0 }, pixel)) return
          const points = Array.isArray(this.points) ? this.points : []
          const coordinate = this.chart.convertFromPixel({ gridIndex: 0 }, pixel)
          const axisValue = coordinate && Number(coordinate[0])
          if (!points.length || !Number.isFinite(axisValue)) return
          // 曲线超过80点时不显示点标记，按横轴命中最近采样点，缩放后仍沿用原始索引。
          const dataIndex = this.qualityMode
            ? points.reduce((nearest, point, index) => Math.abs(Number(point.timeMs) - axisValue) < Math.abs(Number(points[nearest].timeMs) - axisValue) ? index : nearest, 0)
            : Math.max(0, Math.min(points.length - 1, Math.round(axisValue)))
          const point = points[dataIndex]
          if (!point) return
          this.$emit('point-click', { index: dataIndex, point })
        })
      }
      return this.chart
    },
    formatAxisLabel(timeStr) {
      if (!timeStr) return ''
      const parts = String(timeStr).trim().split(/\s+/)
      if (parts.length < 2) return timeStr
      return `${parts[0].replace(/-/g, '.')}\n${parts[1]}`
    },
    buildOverspeedMarkLine(limit) {
      return {
        silent: true,
        symbol: ['none', 'none'],
        lineStyle: {
          color: OVERSPEED_LINE_COLOR,
          width: 2,
          type: 'solid'
        },
        label: {
          show: true,
          position: 'insideEndTop',
          formatter: `${limit} km/h`,
          color: OVERSPEED_LINE_COLOR,
          fontSize: 11
        },
        data: [{ yAxis: limit, name: '超速阈值' }]
      }
    },
    buildQualityOption(points) {
      const { data, gaps, anomalies } = buildQualityChartSeries(points, this.overspeedLimit)
      return {
        tooltip: { trigger: 'axis', formatter: params => {
          if (!params || !params.length) return ''
          return params.filter(item => item.data && item.data.pointKey && (item.data.isAnomaly || item.data.value[1] != null)).map(item => {
            const point = item.data
            const time = new Date(point.value[0]).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
            const speed = point.isAnomaly || point.value[1] == null ? point.qualityText : `${point.value[1]} km/h${point.qualityText === '超速' ? '（超速）' : ''}`
            return `${time}<br/>${speed}${point.isAnomaly ? '（质量标记，不代表0速度）' : ''}`
          }).join('<br/>')
        } },
        grid: { left: 48, right: 30, top: 30, bottom: 55 },
        xAxis: { type: 'time' }, yAxis: [{ type: 'value', min: 0, name: 'km/h' }, { type: 'value', min: 0, max: 1.15, show: false }],
        dataZoom: [{ type: 'inside' }, { type: 'slider', bottom: 4 }],
        series: [{ type: 'line', connectNulls: false, showSymbol: true, lineStyle: { color: '#409EFF' }, data,
          markLine: this.buildOverspeedMarkLine(this.overspeedLimit),
          markArea: { silent: true, itemStyle: { color: 'rgba(144,147,153,.22)' }, data: gaps } },
          { name: '质量标记（非速度）', type: 'scatter', yAxisIndex: 1, symbol: 'triangle', symbolSize: 12, itemStyle: { color: '#E6A23C' }, label: { show: true, position: 'top', formatter: params => params.data.qualityText, fontSize: 10 }, data: anomalies }]
      }
    },
    buildOption(points) {
      if (this.qualityMode) return this.buildQualityOption(points)
      const times = points.map((p) => p.time)
      const speeds = points.map((p) => p.speed)
      const maxSpeed = speeds.length ? Math.max(...speeds) : 0
      const minSpeed = speeds.length ? Math.min(...speeds) : 0
      const limit = this.overspeedLimit
      const yMax = Math.max(limit, Math.ceil(maxSpeed / 20) * 20)
      const yMin = Math.max(0, Math.floor(minSpeed / 20) * 20)
      const showDataZoom = points.length > 1
      const axisLabelStyle = { color: '#909399', fontSize: 10, lineHeight: 14 }
      const yAxisCommon = {
        type: 'value',
        min: yMin,
        max: yMax,
        interval: 20,
        splitLine: { show: true, lineStyle: { color: '#e4e7ed', type: 'dotted' } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: axisLabelStyle
      }
      return {
        color: ['#E53935'],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'line', lineStyle: { color: '#E53935', width: 1 } },
          backgroundColor: 'rgba(255, 255, 255, 0.96)',
          borderColor: '#e4e7ed',
          borderWidth: 1,
          textStyle: { color: '#303133', fontSize: 12 },
          formatter: (params) => {
            const item = params && params[0]
            if (!item) return ''
            const speed = item.data != null ? Number(item.data) : 0
            const speedText = Number.isFinite(speed)
              ? (speed % 1 === 0 ? String(speed) : speed.toFixed(1))
              : '0'
            return `时间：${item.axisValue}<br/>速度：${speedText} km/h`
          }
        },
        grid: {
          left: 48,
          right: 48,
          top: 40,
          bottom: showDataZoom ? 72 : 56,
          containLabel: false
        },
        dataZoom: showDataZoom
          ? [
              {
                type: 'slider',
                xAxisIndex: 0,
                start: this.zoomStart,
                end: this.zoomEnd,
                bottom: 4,
                height: 22,
                borderColor: 'transparent',
                backgroundColor: '#f5f7fa',
                fillerColor: 'rgba(70, 124, 253, 0.18)',
                handleSize: '80%',
                handleStyle: { color: '#fff', borderColor: '#467cfd', borderWidth: 1 },
                dataBackground: {
                  lineStyle: { color: '#E53935', width: 1, opacity: 0.45 },
                  areaStyle: { color: 'rgba(70, 124, 253, 0.12)' }
                },
                selectedDataBackground: {
                  lineStyle: { color: '#E53935', width: 1.2 },
                  areaStyle: { color: 'rgba(70, 124, 253, 0.22)' }
                },
                textStyle: { color: '#909399', fontSize: 10 },
                brushSelect: false
              },
              {
                type: 'inside',
                xAxisIndex: 0,
                start: this.zoomStart,
                end: this.zoomEnd,
                zoomOnMouseWheel: true,
                moveOnMouseMove: true,
                moveOnMouseWheel: false
              }
            ]
          : [],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: times,
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisTick: { show: false },
          axisLabel: {
            ...axisLabelStyle,
            interval: 'auto',
            formatter: (value) => this.formatAxisLabel(value)
          }
        },
        yAxis: [
          { ...yAxisCommon, position: 'left' },
          { ...yAxisCommon, position: 'right' }
        ],
        series: [
          {
            name: '速度',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 4,
            showSymbol: speeds.length <= 80,
            lineStyle: { width: 2, color: '#E53935' },
            itemStyle: { color: '#E53935', borderColor: '#fff', borderWidth: 1 },
            cursor: 'pointer',
            emphasis: {
              scale: true,
              focus: 'series',
              itemStyle: { color: '#E53935', borderColor: '#fff', borderWidth: 2 }
            },
            data: speeds,
            markLine: this.buildOverspeedMarkLine(limit)
          }
        ]
      }
    },
    renderChart() {
      if (!this.active) return
      this.$nextTick(() => {
        const chart = this.initChart()
        if (!chart) return
        const points = Array.isArray(this.points) ? this.points : []
        if (!points.length) {
          chart.clear()
          chart.setOption({
            title: {
              text: '暂无速度数据',
              left: 'center',
              top: 'middle',
              textStyle: { color: '#909399', fontSize: 14, fontWeight: 'normal' }
            },
            xAxis: { show: false },
            yAxis: { show: false },
            series: []
          }, true)
          return
        }
        chart.setOption(this.buildOption(points), true)
        chart.resize()
      })
    }
  }
}
</script>

<style scoped>
.speed-curve-panel {
  position: relative;
  width: 100%;
}

.speed-curve-panel__toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.375rem;
}

.speed-curve-panel__settings {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--layout-primary, #087dff);
  font-size: 0.8125rem;
  cursor: pointer;
}

.speed-curve-panel__settings:hover {
  color: var(--layout-primary-hover, #0670e6);
}

.speed-curve-panel__chart {
  width: 100%;
  min-height: 9rem;
  background: #fff;
  border-radius: 0.25rem;
}
</style>
